import { useChainStore } from "@/stores/chain";
import { enabledChains } from "@/lib/chains";
import { useSwitchChain, useChainId } from "wagmi";

export function ChainSwitcher() {
  const chains = enabledChains();
  const active = useChainStore((s) => s.activeChainId);
  const setActive = useChainStore((s) => s.setActiveChainId);
  const walletChainId = useChainId();
  const { switchChain, isPending } = useSwitchChain();

  if (chains.length === 0) {
    return <span className="text-sm text-muted-foreground">No chains configured</span>;
  }

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setActive(id);
    if (walletChainId !== id) {
      switchChain({ chainId: id });
    }
  };

  // With a single configured chain there's nothing to switch — keep the badge
  // around for context but skip the dropdown.
  if (chains.length === 1) {
    return (
      <span className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground">
        {chains[0]!.name}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* Visual parity with the single-chain badge above: same height,
          font-size, padding, transparent background, content-sized
          width. We drop the heavy `.input` form-field styling (made for
          tall login fields), strip the OS-native chevron with
          `appearance-none`, and paint our own SVG chevron positioned
          absolutely on the right. */}
      <div className="relative inline-flex items-center">
        <select
          className="appearance-none cursor-pointer rounded-md border border-border bg-transparent py-1 pl-2 pr-7 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
          value={active}
          onChange={onChange}
          disabled={isPending}
          aria-label="Active chain"
        >
          {chains.map((c) => (
            <option key={c.chainId} value={c.chainId}>
              {c.name}
            </option>
          ))}
        </select>
        <svg
          aria-hidden
          className="pointer-events-none absolute right-2 h-3 w-3 text-muted-foreground"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 4.5l3 3 3-3" />
        </svg>
      </div>
      {walletChainId !== active && (
        <span className="hidden text-xs text-amber-600 lg:inline">
          Wallet on {walletChainId} — switching…
        </span>
      )}
    </div>
  );
}
